import { useEffect, useMemo, useState } from "react";

import { colorIdentity, type ColorMatch, type PaletteColor } from "../catalog/colors";
import { AddNewColor } from "./AddNewColor";
import {
  ROLE_LABELS,
  SOURCE_ROLES,
  isCorrectable,
  type FolderProblem,
  type GlassesFolderParse,
  type ParsedColor,
  type RoleChoice,
  type RoleOverrides,
  type UnplacedFile,
} from "./glassesFolderParser";
import "./detection.css";

/**
 * What the dashboard found in the dropped folder, shown before anything is sent.
 *
 * The point of this screen is that the administrator confirms our reading of
 * their folder while it is still free to be wrong. Thumbnails are the original
 * files exactly as they sit on disk — drawn straight from the local File with
 * an object URL, never processed here — because the question being asked is
 * "is this the right photograph in the right slot", and a normalised preview
 * would be answering a different question.
 *
 * Full filesystem paths are not shown. They are long, they identify the
 * operator's machine, and they answer nothing: the folder and file names are
 * what distinguish one photograph from another.
 */

/** Colour folder name -> the palette colour the operator chose for it. */
export type ColorAssignments = Record<string, PaletteColor>;

function useObjectUrl(file: File): string {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  // Revoked on unmount: an object URL pins the whole file in memory, and a
  // folder of photographs left pinned across several imports adds up.
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return url;
}

function SourceThumb({ file, label }: { file: File; label: string }) {
  const url = useObjectUrl(file);
  return (
    <figure className="sourceSlot">
      <img className="sourceSlot__img" src={url} alt="" />
      <figcaption className="sourceSlot__cap">
        <span className="sourceSlot__role">{label}</span>
        <span className="sourceSlot__ok" aria-label="found">
          ✓
        </span>
      </figcaption>
    </figure>
  );
}

/**
 * The shop's colours, as one selectable row each.
 *
 * These deliberately do not use the `swatch` class. That name is declared twice
 * in this application with two different meanings — a 16px colour square in
 * products.css, which is what ImagesPanel and VariantsPanel need, and an option
 * chip in dashboard.css — and both rules ship in one bundle, so the square wins
 * and every option here was being squeezed into a 16px box its own contents
 * then had to escape. The layout below is owned by detection.css and shares no
 * class name with either, so neither can reach it again.
 *
 * One row per colour, and the row is the control: the whole row is the click
 * target, not a chip inside it. Two colours can carry the same name, so the hex
 * sits on its own line under it — it is the only thing that tells two Blacks
 * apart, and it must never be the part that gets clipped.
 */
function PaletteChoiceList({
  swatches,
  selected,
  onSelect,
  groupLabel,
}: {
  swatches: readonly PaletteColor[];
  selected: string | null;
  onSelect: (swatch: PaletteColor) => void;
  /** Distinguishes the shortlist from the full palette for assistive readers. */
  groupLabel: string;
}) {
  return (
    <ul className="paletteChoice__list" aria-label={groupLabel}>
      {swatches.map((swatch) => {
        const id = colorIdentity(swatch);
        const isSelected = selected === id;
        return (
          <li key={id} className="paletteChoice">
            <button
              type="button"
              className={
                isSelected
                  ? "paletteChoice__btn is-selected"
                  : "paletteChoice__btn"
              }
              onClick={() => onSelect(swatch)}
              aria-pressed={isSelected}
            >
              <span
                className="paletteChoice__dot"
                style={{ backgroundColor: swatch.swatch_hex }}
                aria-hidden="true"
              />
              <span className="paletteChoice__text">
                <span className="paletteChoice__name">{swatch.name}</span>
                {/* Read out as part of the option's name, so "Black #1A1A1A"
                    and "Black #111111" are two different options to a screen
                    reader as well as to the eye. */}
                <span className="paletteChoice__hex">{swatch.swatch_hex}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The correction path for a colour the folder name did not settle.
 *
 * Two cases reach here and they are told apart on purpose. An ambiguous name
 * means the shop has two approved colours by that name, so the two candidates
 * are offered first — the answer is almost certainly one of them. An unknown
 * name means the shop has no such colour at all, so the whole approved palette
 * is offered. Neither case picks anything on the operator's behalf.
 */
function ColorSwatchPicker({
  match,
  palette,
  selected,
  onSelect,
}: {
  match: Extract<ColorMatch, { kind: "ambiguous" | "unknown" }>;
  palette: readonly PaletteColor[];
  selected: string | null;
  onSelect: (swatch: PaletteColor) => void;
}) {
  const ambiguous = match.kind === "ambiguous";
  return (
    <div className="paletteChoice__panel">
      <p className="paletteChoice__prompt">
        {ambiguous ? (
          <>
            The folder is named <strong>{match.detectedName}</strong>, and the shop has{" "}
            {match.candidates.length} colours by that name. Choose which one these are:
          </>
        ) : (
          <>
            The folder is named <strong>{match.detectedName}</strong>, which is not one
            of the shop's colours yet. Choose the colour these are, or add it:
          </>
        )}
      </p>

      {/* Ambiguity offers the matching shades first — the answer is almost
          certainly one of them — with the rest a click away. */}
      <PaletteChoiceList
        swatches={ambiguous ? match.candidates : palette}
        selected={selected}
        onSelect={onSelect}
        groupLabel={
          ambiguous
            ? `Colours named ${match.detectedName}`
            : "The shop's colours"
        }
      />

      {ambiguous ? (
        // On its own row, below the shortlist, and it opens in place: the full
        // palette renders into the same list layout rather than a second one.
        <details className="paletteChoice__more">
          <summary className="paletteChoice__moreSummary">Show all colours</summary>
          <div className="paletteChoice__moreBody">
            <PaletteChoiceList
              swatches={palette}
              selected={selected}
              onSelect={onSelect}
              groupLabel="All of the shop's colours"
            />
          </div>
        </details>
      ) : null}

      {!ambiguous ? (
        <div className="paletteChoice__add">
          <AddNewColor detectedName={match.detectedName} onAdded={onSelect} />
        </div>
      ) : null}
    </div>
  );
}

const UNPLACED_REASON: Record<UnplacedFile["reason"], string> = {
  ambiguous: "names more than one view",
  unrecognized: "does not say which view it is",
  duplicate: "claims a view another photograph also claims",
};

/**
 * The answer to a view the parser would not guess.
 *
 * A plain select per file, listing the three views and "Do not use". The parser
 * refuses to choose between two candidates because choosing wrong puts the
 * wrong photograph in front of a shopper; the operator can simply look at the
 * thumbnail and say. "Do not use" is what resolves a duplicate: one of the two
 * is the real Side view and the other is a file that should not be imported.
 */
function RoleFix({
  items,
  overrides,
  onChoose,
}: {
  items: readonly UnplacedFile[];
  overrides: RoleOverrides;
  onChoose: (relativePath: string, choice: RoleChoice | null) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="roleFix">
      <p className="roleFix__title">
        {items.length === 1
          ? "One photograph needs its view"
          : `${items.length} photographs need their view`}
      </p>
      {items.map((item) => (
        <RoleFixRow
          key={item.file.relativePath}
          item={item}
          choice={overrides[item.file.relativePath] ?? ""}
          onChoose={(choice) => onChoose(item.file.relativePath, choice)}
        />
      ))}
    </div>
  );
}

function RoleFixRow({
  item,
  choice,
  onChoose,
}: {
  item: UnplacedFile;
  choice: RoleChoice | "";
  onChoose: (choice: RoleChoice | null) => void;
}) {
  const url = useObjectUrl(item.file.file);
  const label = `View for ${item.file.name}`;
  return (
    <div className="roleFix__row">
      <img className="roleFix__thumb" src={url} alt="" />
      <span className="roleFix__file">
        {item.file.name}
        <span className="roleFix__why">
          {UNPLACED_REASON[item.reason]}
          {item.reason === "duplicate" && item.role
            ? ` (${ROLE_LABELS[item.role]})`
            : ""}
        </span>
      </span>
      <select
        className="input roleFix__select"
        aria-label={label}
        value={choice}
        onChange={(event) =>
          onChoose(event.target.value === "" ? null : (event.target.value as RoleChoice))
        }
      >
        <option value="">Choose a view…</option>
        {SOURCE_ROLES.map((role) => (
          <option key={role} value={role}>
            {ROLE_LABELS[role]}
          </option>
        ))}
        <option value="unused">Do not use this photograph</option>
      </select>
    </div>
  );
}

function ColorCard({
  color,
  palette,
  assigned,
  onAssign,
  faults,
  overrides,
  onChooseRole,
}: {
  color: ParsedColor;
  palette: readonly PaletteColor[];
  assigned: PaletteColor | null;
  onAssign: (swatch: PaletteColor) => void;
  /** The faults that belong to this colour, shown on it rather than elsewhere. */
  faults: readonly FolderProblem[];
  overrides: RoleOverrides;
  onChooseRole: (relativePath: string, choice: RoleChoice | null) => void;
}) {
  const matched = color.color.kind === "matched" ? color.color.color : null;
  const resolved = matched ?? assigned;

  return (
    <article className={resolved ? "colorCard" : "colorCard is-attention"}>
      <header className="colorCard__head">
        {resolved ? (
          <span
            className="swatch__dot"
            style={{ backgroundColor: resolved.swatch_hex }}
            aria-hidden="true"
          />
        ) : null}
        <h4 className="colorCard__name">
          {resolved ? resolved.name : color.folderName}
        </h4>
        {!resolved ? <span className="tag tag--warn">Needs attention</span> : null}
      </header>

      <div className="colorCard__sources">
        {SOURCE_ROLES.map((role) => {
          const source = color.sources.find((s) => s.role === role);
          return source ? (
            <SourceThumb
              key={role}
              file={source.file.file}
              label={ROLE_LABELS[role]}
            />
          ) : (
            <div key={role} className="sourceSlot sourceSlot--missing">
              <span className="sourceSlot__role">{ROLE_LABELS[role]}</span>
              <span className="sourceSlot__missing">Missing</span>
            </div>
          );
        })}
      </div>

      {/* Beside the colour they belong to, not in a list further down the page:
          "orange has no Side photograph" is a fact about orange. */}
      {faults.length > 0 ? (
        <ul className="colorCard__faults">
          {faults.map((fault, index) => (
            <li key={`${fault.code}-${index}`}>{fault.message}</li>
          ))}
        </ul>
      ) : null}

      <RoleFix
        items={color.unplaced}
        overrides={overrides}
        onChoose={onChooseRole}
      />

      {color.color.kind !== "matched" ? (
        <ColorSwatchPicker
          match={color.color}
          palette={palette}
          selected={assigned ? colorIdentity(assigned) : null}
          onSelect={onAssign}
        />
      ) : null}
    </article>
  );
}

export function DetectionReview({
  parse,
  palette = [],
  onAssignmentsChange,
  overrides = {},
  onOverridesChange,
}: {
  parse: GlassesFolderParse;
  palette?: readonly PaletteColor[];
  onAssignmentsChange?: (assignments: ColorAssignments) => void;
  /** Views the administrator has assigned by hand, keyed by relative path. */
  overrides?: RoleOverrides;
  onOverridesChange?: (next: RoleOverrides) => void;
}) {
  const [assignments, setAssignments] = useState<ColorAssignments>({});

  const assign = (folderName: string, swatch: PaletteColor) => {
    const next = { ...assignments, [folderName]: swatch };
    setAssignments(next);
    onAssignmentsChange?.(next);
  };

  const chooseRole = (relativePath: string, choice: RoleChoice | null) => {
    const next = { ...overrides };
    // Back to "Choose a view…" genuinely un-answers it, rather than leaving a
    // stale assignment the operator can no longer see.
    if (choice === null) delete next[relativePath];
    else next[relativePath] = choice;
    onOverridesChange?.(next);
  };

  // Both branches show the same colour cards. A folder with a fault in it is
  // still the operator's folder, and hiding the colours behind a list of
  // sentences was what made a wrong view impossible to correct.
  const rootName = parse.ok ? parse.product.rootName : parse.rootName;
  const colors: ParsedColor[] = parse.ok ? parse.product.colors : (parse.colors ?? []);
  const problems: FolderProblem[] = parse.ok ? [] : parse.problems;

  /**
   * The faults worth stating in words on a colour card.
   *
   * A file whose view is unsettled is already presented by the control that
   * settles it — repeating the sentence beside it would say the same thing
   * twice. A missing photograph has no control, because there is nothing to
   * assign, so it is the one that has to be said.
   */
  const perColour = (folderName: string) =>
    problems.filter(
      (problem) =>
        problem.colorFolder === folderName && problem.code === "missing_role",
    );

  // Faults nobody can answer on this screen: they belong to the folder, not to
  // a colour, and the folder has to be chosen again.
  const blocking = problems.filter(
    (problem) => !isCorrectable(problem) || !problem.colorFolder,
  );

  if (colors.length === 0) {
    return (
      <section className="review" aria-label="Folder problems">
        <h3 className="review__title">
          {rootName
            ? `"${rootName}" cannot be imported yet`
            : "That folder cannot be imported"}
        </h3>
        <p className="review__note">
          Nothing has been uploaded. Fix these and choose the folder again:
        </p>
        <ul className="problemList">
          {problems.map((problem, i) => (
            <li key={`${problem.code}-${i}`} className="problemList__item">
              {problem.colorFolder ? (
                <span className="problemList__where">{problem.colorFolder}</span>
              ) : null}
              {problem.message}
            </li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <section className="review" aria-label="Folder detection">
      <h3 className="review__title">{rootName}</h3>
      <p className="review__note">
        {colors.length} {colors.length === 1 ? "colour" : "colours"} detected. Check
        each one before importing — nothing has been uploaded yet.
      </p>

      {blocking.length > 0 ? (
        <ul className="problemList review__blocking" aria-label="Folder problems">
          {blocking.map((problem, i) => (
            <li key={`${problem.code}-${i}`} className="problemList__item">
              {problem.colorFolder ? (
                <span className="problemList__where">{problem.colorFolder}</span>
              ) : null}
              {problem.message}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="colorCards">
        {colors.map((color) => (
          <ColorCard
            key={color.folderName}
            color={color}
            palette={palette}
            assigned={assignments[color.folderName] ?? null}
            onAssign={(swatch) => assign(color.folderName, swatch)}
            faults={perColour(color.folderName)}
            overrides={overrides}
            onChooseRole={chooseRole}
          />
        ))}
      </div>
    </section>
  );
}
